import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CREDIT_CURRENCIES, type CreditCustomer } from '@/lib/credit';

interface Props {
  customer?: CreditCustomer | null;
  currency: string;
  onClose: () => void;
  onSubmit: (data: Partial<CreditCustomer>) => Promise<void>;
}

const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

export default function CustomerFormModal({ customer, currency, onClose, onSubmit }: Props) {
  const { t } = useTranslation();
  const [fullName, setFullName] = useState(customer?.full_name || '');
  const [phone, setPhone] = useState(customer?.phone || '');
  const [creditLimit, setCreditLimit] = useState(customer?.credit_limit != null ? String(customer.credit_limit) : '');
  const [usualDate, setUsualDate] = useState(customer?.usual_payment_date || '');
  const [email, setEmail] = useState((customer as (CreditCustomer & { email?: string }))?.email || '');
  const [notes, setNotes] = useState(customer?.notes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const unit = CREDIT_CURRENCIES.includes(currency) ? currency : 'MAD';

  const handleSubmit = async () => {
    if (!fullName.trim()) {
      setError(t('crf.name_required', { defaultValue: 'Le nom du client est obligatoire.' }));
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSubmit({
        full_name: fullName.trim(),
        phone: phone.trim() || null,
        credit_limit: creditLimit.trim() ? parseFloat(creditLimit.replace(',', '.')) : null,
        usual_payment_date: usualDate || null,
        notes: notes.trim() || null,
        ...(email.trim() ? { email: email.trim() } : {}),
      } as Partial<CreditCustomer>);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('crf.save_error', { defaultValue: 'Erreur lors de l\u2019enregistrement' }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={saving ? undefined : onClose} />
      <div className="fixed z-50 inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center sm:p-4">
        <div className="w-full sm:max-w-md bg-background-50 rounded-t-2xl sm:rounded-2xl border border-background-200/70 max-h-[92vh] overflow-y-auto">
          <div className="flex items-center justify-between px-5 h-14 border-b border-background-200/70 sticky top-0 bg-background-50 z-10">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center">
                <i className="ri-user-add-line text-lg"></i>
              </div>
              <h3 className="text-sm font-bold text-foreground-950">
                {customer ? t('crf.edit_customer', { defaultValue: 'Modifier le client' }) : t('crf.new_customer', { defaultValue: 'Nouveau client' })}
              </h3>
            </div>
            <button
              onClick={onClose}
              disabled={saving}
              className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-40"
            >
              <i className="ri-close-line text-foreground-500"></i>
            </button>
          </div>

          <div className="p-5 space-y-4">
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
                {t('crf.customer_name', { defaultValue: 'Nom du client' })} <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                autoFocus
                value={fullName}
                onChange={(e) => { setFullName(e.target.value); setError(''); }}
                placeholder={t('crf.customer_name_ph', { defaultValue: 'Ex : Mohammed Alami' })}
                className="w-full h-11 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300"
              />
              <p className="text-[11px] text-foreground-400 mt-1.5">
                {t('crf.customer_hint', { defaultValue: 'Aucun compte Zifek requis. Le client pourra être invité plus tard pour suivre son solde.' })}
              </p>
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">{t('crf.phone_optional', { defaultValue: 'Téléphone (optionnel)' })}</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+212 6 12 34 56 78"
                className="w-full h-11 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">{t('crf.credit_limit_optional', { defaultValue: 'Plafond de crédit (optionnel)' })}</label>
                <div className="relative">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={creditLimit}
                    onChange={(e) => setCreditLimit(e.target.value)}
                    placeholder="Ex : 500"
                    className="w-full h-11 px-3 pr-14 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-foreground-400 font-medium">{unit}</span>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">{t('crf.usual_payment_date', { defaultValue: 'Date de paiement habituelle' })}</label>
                <select
                  value={usualDate}
                  onChange={(e) => setUsualDate(e.target.value)}
                  className="w-full h-11 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300"
                >
                  <option value="">{t('crf.not_defined', { defaultValue: 'Non définie' })}</option>
                  {DAYS.map((d) => (
                    <option key={d} value={String(d)}>{`Le ${d} du mois`}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">{t('crf.notes_optional', { defaultValue: 'Notes (optionnel)' })}</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder={t('crf.notes_ph', { defaultValue: 'Ex : voisin du quartier, paie fin de mois…' })}
                className="w-full px-3 py-2 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 resize-none"
              />
            </div>

            {error && (
              <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>
            )}
          </div>

          <div className="px-5 pb-5 pt-1 sticky bottom-0 bg-background-50">
            <button
              onClick={handleSubmit}
              disabled={saving || !fullName.trim()}
              className="w-full h-12 rounded-full text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer flex items-center justify-center gap-2 bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <><i className="ri-loader-4-line animate-spin"></i>{t('crf.saving', { defaultValue: 'Enregistrement...' })}</>
              ) : (
                <><i className="ri-save-line"></i>{customer ? t('prf.save', { defaultValue: 'Enregistrer' }) : t('crf.create_customer', { defaultValue: 'Créer le client' })}</>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}